import React, { useRef, useState, useEffect, useMemo, useCallback, memo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, MapPin, Clock, Users, Ticket, CalendarDays, Video, Mail, Phone, Play, X, ArrowLeft } from "lucide-react";
import { convertYouTubeUrlToEmbed } from "../../EventSetUp/EventLandingBuilder/utils/videoUtils";
import "../../EventSetUp/EventLandingBuilder/EventLandingBuilderStylesTwo.css";
import usePost from "../../../Hooks/usePost";
import useCustomFecthQuery from "../../../Hooks/useCustomFecthQuery";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import CalendarDropdown from "../CalendarDropdown/CalendarDropdown";
import { prepareEventData } from "../../../utils/calendarUtils";
import {
  mapCustomFieldValues,
  validateRequiredCheckboxes,
} from "../../../constants/optInDisclaimerFields";
import PrivacyDisclaimerText from "../EventRegistration/PrivacyDisclaimerText";

// Color schemes matching the builder
const colorSchemes = {
  vibrant: {
    id: "vibrant",
    name: "Vibrant Orange",
    colors: ["#f97316", "#fb923c", "#fdba74", "#fed7aa"],
  },
  professional: {
    id: "professional",
    name: "Professional Blue",
    colors: ["#3b82f6", "#60a5fa", "#93c5fd", "#dbeafe"],
  },
  elegant: {
    id: "elegant",
    name: "Elegant Purple",
    colors: ["#8b5cf6", "#a78bfa", "#c4b5fd", "#e9d5ff"],
  },
  nature: {
    id: "nature",
    name: "Nature Green",
    colors: ["#22c55e", "#4ade80", "#86efac", "#bbf7d0"],
  },
  sunset: {
    id: "sunset",
    name: "Sunset Pink",
    colors: ["#f72585", "#ff006e", "#fb8500", "#ffb703"],
  },
};

const CustomLandingPage = ({
  eventData,
  colorScheme,
  templateId,
  registrationForm = [],
  eventID,
  eventName,
  isBuilderPreview = false
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const registrationFormRef = useRef(null);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const { execute, pending, status } = usePost();
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [shouldLoadHeavySections, setShouldLoadHeavySections] = useState(false);

  // Helper function to format camelCase to Title Case - memoized
  const formatLabel = useCallback((label, name) => {
    if (label && label !== name) {
      return label;
    }
    if (name) {
      return name
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    }
    return label || name || '';
  }, []);

  const [countdown, setCountdown] = useState(null);
  const [eventInPast, setEventInPast] = useState(false);
  const [eventIsDone, setEventIsDone] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [isSpeakerModalOpen, setIsSpeakerModalOpen] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('overview');
  const aboutRef = useRef(null);
  const agendaRef = useRef(null);
  const speakersRef = useRef(null);
  const scheduleRef = useRef(null);
  const locationRef = useRef(null);
  const supportRef = useRef(null);

  // When this is true, new registrations should not be allowed
  const registrationClosed = eventIsDone || eventInPast;

  // Get event ID and name from props or URL params
  const currentEventID = eventID || params?.eventID;
  const currentEventName = eventName || params?.eventName || eventData?.name || eventData?.title || "event";

  // Fetch registration form fields from backend
  const {
    data: registrationFormData,
    isLoading: isLoadingRegistrationForm,
  } = useCustomFecthQuery(
    ["eventForm", currentEventID],
    `${process.env.REACT_APP_API}event-form/${currentEventID}`,
    null,
    { enabled: !!currentEventID }
  );

  // Use either the prop or the fetched data
  const effectiveRegistrationForm = useMemo(() => {
    if (registrationForm && registrationForm.length > 0) {
      return registrationForm;
    }
    return registrationFormData?.data?.[0]?.customFields || [];
  }, [registrationForm, registrationFormData]);

  // Handle responsive behavior - optimized with debounce
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 150);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Lazy load heavy sections after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadHeavySections(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const isMobile = windowWidth < 768;
  const isDesktop = windowWidth >= 768;

  // Handle speaker card click to open modal - memoized
  const handleSpeakerClick = useCallback((speaker) => {
    setSelectedSpeaker(speaker);
    setIsSpeakerModalOpen(true);
  }, []);

  // Close speaker modal - memoized
  const closeSpeakerModal = useCallback(() => {
    setIsSpeakerModalOpen(false);
    setSelectedSpeaker(null);
  }, []);

  // Check if registration form exists on the page - memoized
  const hasRegistrationForm = useMemo(() => effectiveRegistrationForm && effectiveRegistrationForm.length > 0, [effectiveRegistrationForm]);

  // Speaker layout option - memoized
  const speakerLayout = useMemo(() => eventData?.speakerLayout || "horizontal-card", [eventData?.speakerLayout]);

  // Load sponsors from API
  let token = null;
  try {
    const authData = localStorage.getItem("authEventKagoUser");
    if (authData) {
      const obj = JSON.parse(authData);
      token = obj?.data?.token;
    }
  } catch (error) {
    console.error("Error reading auth data for sponsors:", error);
  }

  const {
    data: sponsorsDataFromApi,
  } = useCustomFecthQuery(
    ["mySponsorsLanding", currentEventID],
    `${process.env.REACT_APP_API}sponsors/event/${currentEventID}`,
    token,
    { enabled: !!currentEventID }
  );

  const handleFieldChange = useCallback((name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  }, []);

  // Set up countdown timer
  useEffect(() => {
    if (!eventData?.date) {
      setCountdown(null);
      return;
    }
    let eventStart;
    let eventEnd = null;
    try {
      eventStart = new Date(eventData.date);
      if (eventData.startTime) {
        const [h, m] = eventData.startTime.split(":").map(Number);
        eventStart.setHours(h || 0, m || 0, 0, 0);
      }
      // Derive an approximate event end time if available
      eventEnd = new Date(eventStart);
      if (eventData.endTime) {
        const [endH, endM] = eventData.endTime.split(":").map(Number);
        eventEnd.setHours(endH || 0, endM || 0, 0, 0);
      }
    } catch {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      if (eventEnd && now > eventEnd) {
        setEventIsDone(true);
      } else {
        setEventIsDone(false);
      }
      const diff = eventStart.getTime() - now.getTime();
      if (diff <= 0) {
        setEventInPast(true);
        setCountdown(null);
        return;
      }
      setEventInPast(false);
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  }, [eventData?.date, eventData?.startTime, eventData?.endDate, eventData?.endTime]);

  // Watch for successful registration
  useEffect(() => {
    if (status === "success") {
      setRegistrationSuccess(true);
      setFormValues({});
      setFormErrors({});
    }
  }, [status]);

  // Function to check if a session is currently live - memoized
  const isSessionLive = useCallback((session) => {
    if (!session || !session.date || !session.startTime || !session.endTime) {
      return false;
    }
    try {
      const now = new Date();
      const sessionDate = new Date(session.date);
      const [startHours, startMinutes] = session.startTime.split(':').map(Number);
      const [endHours, endMinutes] = session.endTime.split(':').map(Number);
      const startDateTime = new Date(sessionDate);
      startDateTime.setHours(startHours, startMinutes, 0, 0);
      const endDateTime = new Date(sessionDate);
      endDateTime.setHours(endHours, endMinutes, 0, 0);
      return now >= startDateTime && now <= endDateTime;
    } catch (error) {
      console.error('Error checking if session is live:', error);
      return false;
    }
  }, []);

  // Function to handle joining a session - memoized
  const handleJoinSession = useCallback((session) => {
    if (!session.sessionID && !session._id) {
      console.error('Session ID not available');
      return;
    }
    const sessionID = session.sessionID || session._id;
    const sessionName = (session.title || session.name || 'Session').replace(/\s+/g, '-');
    const formattedEventName = currentEventName.replace(/\s+/g, '-');
    navigate(`/live-session/attendee/${formattedEventName}/${sessionName}/${currentEventID}/${sessionID}`);
  }, [currentEventName, currentEventID, navigate]);

  // Compute location string and map embed URL
  const locationString =
    eventData.location ||
    [eventData.venue, eventData.city, eventData.country].filter(Boolean).join(", ") ||
    "";
  const mapEmbedUrl = eventData.mapEmbedUrl
    ? eventData.mapEmbedUrl
    : locationString
      ? `https://www.google.com/maps?q=${encodeURIComponent(locationString)}&output=embed`
      : null;

  // Handle Register Now button click - memoized
  const handleRegisterNow = useCallback(() => {
    // Safety guard: if registration is closed, do nothing
    if (eventIsDone || eventInPast) {
      return;
    }

    if (hasRegistrationForm && registrationFormRef.current) {
      registrationFormRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      return;
    }
    if (!currentEventID) {
      console.error('[CustomLandingPage] Event ID not found, cannot redirect to registration');
      return;
    }
    const formattedEventName = currentEventName.replace(/\s+/g, "-");
    const registrationPath = `/${formattedEventName}/registration/${currentEventID}`;
    navigate(registrationPath);
  }, [hasRegistrationForm, currentEventID, currentEventName, navigate]);

  // Handle inline registration form submit
  const handleInlineRegistrationSubmit = async () => {
    if (!currentEventID) {
      console.error("[CustomLandingPage] Event ID not found, cannot submit registration");
      setRegistrationError("Event ID not found. Please refresh the page and try again.");
      return;
    }
    setRegistrationError(null);
    setRegistrationSuccess(false);
    const requiredFieldNames = ["firstName", "lastName", "email", "phoneNumber"];
    const newErrors = {};
    effectiveRegistrationForm.forEach((field) => {
      if (requiredFieldNames.includes(field.name) && !formValues[field.name]) {
        newErrors[field.name] = `${field.label || field.name} is required`;
      }
    });
    Object.assign(
      newErrors,
      validateRequiredCheckboxes(effectiveRegistrationForm, formValues)
    );
    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      setRegistrationError("Please fill in all required fields.");
      return;
    }
    const customFields = mapCustomFieldValues(effectiveRegistrationForm, formValues);
    const registrationData = {
      firstName: formValues.firstName || "",
      lastName: formValues.lastName || "",
      email: formValues.email || "",
      phoneNumber: formValues.phoneNumber || "",
      customFields,
      communicationConsent: true,
    };
    try {
      await execute(
        `attendees/add/${currentEventID}`,
        JSON.stringify(registrationData),
        "POST",
        "Successfully Registered. Please check your email for event entrance details",
        null,
        true,
        true
      );
      setRegistrationSuccess(true);
      setRegistrationError(null);
      setFormValues({});
      setFormErrors({});
      if (registrationFormRef.current) {
        registrationFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } catch (error) {
      console.error("[CustomLandingPage] Failed to submit registration:", error);
      setRegistrationSuccess(false);
      let errorMessage = "Registration failed. Please try again.";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      setRegistrationError(errorMessage);
      if (registrationFormRef.current) {
        registrationFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  // Helper: open a specific agenda item in user's Google Calendar - memoized
  const addAgendaItemToCalendar = useCallback((item) => {
    try {
      if (!eventData?.date || !item?.startTime) return;
      const baseDate = new Date(eventData.date);
      if (isNaN(baseDate.getTime())) return;
      const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const match = timeStr.match(/(\d{1,2}):?(\d{2})?/);
        if (!match) return null;
        const h = parseInt(match[1], 10);
        const m = match[2] ? parseInt(match[2], 10) : 0;
        if (isNaN(h) || isNaN(m)) return null;
        return { h, m };
      };
      const startParsed = parseTime(item.startTime);
      const endParsed = parseTime(item.endTime);
      if (!startParsed) return;
      const start = new Date(baseDate);
      start.setHours(startParsed.h, startParsed.m, 0, 0);
      let end = new Date(start);
      if (endParsed) {
        end.setHours(endParsed.h, endParsed.m, 0, 0);
      } else {
        end = new Date(start.getTime() + 60 * 60 * 1000);
      }
      const formatForGoogle = (dt) =>
        dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
      const datesParam = `${formatForGoogle(start)}/${formatForGoogle(end)}`;
      const title =
        item.title ||
        `${eventData.name || eventData.title || "Event agenda item"}`;
      const details = item.description || eventData.description || "";
      const location =
        locationString || eventData.location || eventData.venue || "";
      const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
        title
      )}&dates=${encodeURIComponent(datesParam)}&details=${encodeURIComponent(
        details
      )}&location=${encodeURIComponent(location)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to open agenda item in calendar", err);
    }
  }, [eventData, locationString]);

  // Handle colorScheme - memoized
  const colors = useMemo(() => {
    if (typeof colorScheme === "string") {
      const scheme = colorSchemes[colorScheme];
      return scheme ? scheme.colors : colorSchemes.vibrant.colors;
    } else if (colorScheme?.colors) {
      return colorScheme.colors;
    } else if (Array.isArray(colorScheme)) {
      return colorScheme;
    } else {
      return colorSchemes.vibrant.colors;
    }
  }, [colorScheme]);

  // Data for top navigation bar - memoized
  const headerLogo = useMemo(() => eventData?.logoImage || eventData?.logo || null, [eventData?.logoImage, eventData?.logo]);

  // Determine which sections exist - memoized
  const sectionFlags = useMemo(() => {
    const isInPersonEvent = eventData?.eventFormat !== 'Virtual';
    const hasSpeakers = eventData?.speakers && Array.isArray(eventData.speakers) && eventData.speakers.length > 0;
    const hasSchedule = eventData?.schedule && Array.isArray(eventData.schedule) && eventData.schedule.length > 0;
    const hasAgenda = isInPersonEvent && Array.isArray(eventData?.agenda) && eventData.agenda.length > 0;
    const hasLocation = !!locationString;
    const hasSupport = !!(
      (eventData?.supportMail && eventData.supportMail.trim()) ||
      (eventData?.supportPhoneNumber && eventData.supportPhoneNumber.trim())
    );
    return { isInPersonEvent, hasSpeakers, hasSchedule, hasAgenda, hasLocation, hasSupport };
  }, [eventData, locationString]);

  // Build nav items - memoized
  const navItems = useMemo(() => {
    const allNavItems = [
      { id: 'about-us', label: 'About', show: true },
      { id: 'agenda', label: 'Agenda', show: sectionFlags.hasAgenda },
      { id: 'speakers', label: 'Speakers', show: sectionFlags.hasSpeakers },
      { id: 'schedule', label: 'Schedule', show: sectionFlags.hasSchedule },
      { id: 'location', label: 'Location', show: sectionFlags.hasLocation },
    ];
    return allNavItems.filter(item => item.show);
  }, [sectionFlags]);

  const scrollToSection = useCallback((id) => {
    let ref = null;
    if (id === 'about-us') ref = aboutRef;
    if (id === 'agenda') ref = agendaRef;
    if (id === 'speakers') ref = speakersRef;
    if (id === 'schedule') ref = scheduleRef;
    if (id === 'location') ref = locationRef;
    if (id === 'support') ref = supportRef;
    setActiveNavItem(id);
    if (ref && ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  // Keep the "no data" early return AFTER all hooks to satisfy react-hooks/rules-of-hooks
  if (!eventData) {
    return null;
  }

  return (
  <div className="custom-landing-page">
  {/* TOP BAR HEADER - RESPONSIVE */}
  <div style={{
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: isMobile ? '4px 12px' : '10px 24px',
    backgroundColor: '#ffffff',
    borderBottom: '2px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    minHeight: isMobile ? '36px' : '64px',
    boxSizing: 'border-box',
  }}>
    
    {/* LEFT - Logo (Left Corner) */}
    <div style={{ 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'flex-start',
      flex: 1,
      minWidth: 0,
    }}>
      {/* ✅ Conditional Rendering: Show EITHER Image OR Fallback Circle */}
      {headerLogo ? (
        <img
          src={headerLogo}
          alt="Event logo"
          className="event-logo-responsive"
        />
      ) : (
        <div className="event-logo-fallback">
          {currentEventName?.charAt(0)?.toUpperCase() || 'E'}
        </div>
      )}
    </div>

    {/* ✅ CENTER - Desktop Navigation Menu */}
    {isDesktop && navItems.length > 0 && (
      <nav style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 8,
        overflowX: 'auto',
      }}>
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => scrollToSection(item.id)}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'transparent',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: activeNavItem === item.id ? 600 : 500,
              color: activeNavItem === item.id ? (colors[0] || '#f97316') : '#374151',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => {
              if (activeNavItem !== item.id) {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = colors[0] || '#f97316';
              }
            }}
            onMouseOut={(e) => {
              if (activeNavItem !== item.id) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#374151';
              }
            }}
          >
            {item.label}
          </button>
        ))}
      </nav>
    )}

    {/* ✅ RESPONSIVE LOGO STYLES */}
    <style>{`
      /* Image Logo Styles */
      .event-logo-responsive {
        height: 45px !important;  /* Mobile default */
        width: auto !important;
        max-width: 100px !important;
        object-fit: contain;
        display: block !important;
      }
      
      /* Fallback Circle Styles */
      .event-logo-fallback {
        height: 45px;
        width: 45px;
        border-radius: 50%;
        display: flex; /* Always flex to center the letter */
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-weight: 700;
        font-size: 14px;
        background-color: ${colors[0] || '#f97316'};
        flex-shrink: 0;
      }

      /* Tablet and larger */
      @media (min-width: 768px) {
        .event-logo-responsive {
          height: 50px !important;
          max-width: 205px !important;
        }
        
        .event-logo-fallback {
          height: 50px;
          width: 50px;
          font-size: 18px;
        }
      }
      
      /* Large desktop */
      @media (min-width: 1024px) {
        .event-logo-responsive {
          height: 55px !important;
          max-width: 225px !important;
        }
      }
    `}</style>

    {/* RIGHT - Get In Touch (Desktop) + Hamburger Menu (Mobile) */}
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 12,
      flex: 1,
      justifyContent: 'flex-end',
    }}>
      {/* Get In Touch Button - SHOWN ONLY ON DESKTOP when Event Support has contact info */}
      {isDesktop && sectionFlags.hasSupport && (
        <button
          type="button"
          onClick={() => scrollToSection('support')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 20px',
            borderRadius: 999,
            border: 'none',
            backgroundColor: colors[0] || '#f97316',
            color: '#ffffff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 38,
            whiteSpace: 'nowrap',
            transition: 'opacity 0.2s ease',
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          Get In Touch
        </button>
      )}

      {/* Hamburger Menu Button - SHOWN ONLY ON MOBILE */}
      {isMobile && (
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          title="Toggle menu"
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
          type="button"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            width: 70,
            height: 70,
            borderRadius: 6,
            transition: 'background-color 0.2s ease',
            flexShrink: 0,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {isMobileMenuOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors[0] || '#f97316'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors[0] || '#f97316'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
      )}
    </div>

    {/* Mobile Menu Dropdown - SHOWN ONLY ON MOBILE WHEN OPEN */}
    {isMobile && isMobileMenuOpen && (
      <div style={{
        position: 'fixed',
        top: '55px',
        left: 0,
        right: 0,
        backgroundColor: '#ffffff',
        borderBottom: '2px solid #e5e7eb',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        zIndex: 99,
        padding: '14px 18px',
        maxHeight: 'calc(100vh - 36px)',
        overflowY: 'auto',
      }}>
        {navItems.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  scrollToSection(item.id);
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: 10,
                  fontSize: 16,
                  fontWeight: activeNavItem === item.id ? 700 : 500,
                  color: activeNavItem === item.id ? (colors[0] || '#f97316') : '#1f2937',
                  textAlign: 'left',
                  cursor: 'pointer',
                  width: '100%',
                  minHeight: 48,
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
        {/* Get In Touch Button in Mobile Menu - only when Event Support has contact info */}
        {sectionFlags.hasSupport && (
          <button
            type="button"
            onClick={() => {
              scrollToSection('support');
              setIsMobileMenuOpen(false);
            }}
            style={{
              width: '100%',
              padding: '16px 24px',
              borderRadius: 999,
              border: 'none',
              backgroundColor: colors[0] || '#f97316',
              color: '#ffffff',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              minHeight: 48,
              marginTop: 8,
            }}
          >
            Get In Touch
          </button>
        )}
      </div>
    )}
  </div>
  
  {/* ✅ RESPONSIVE LOGO STYLES */}
  <style>{`
    .event-logo {
      height: 45px;
      width: auto;
      max-width: 200px;
      object-fit: contain;
      flex-shrink: 0;
      display: block;
    }
    
    .event-logo-fallback {
      height: 45px;
      width: 45px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-weight: 700;
      font-size: 18px;
      background-color: ${colors[0] || '#f97316'};
      flex-shrink: 0;
    }
    
    /* Mobile styles */
    @media (max-width: 767px) {
      .event-logo {
        height: 28px;
        max-width: 120px;
      }
      
      .event-logo-fallback {
        height: 28px;
        width: 28px;
        font-size: 12px;
      }
    }
    
    /* Tablet breakpoint */
    @media (min-width: 768px) and (max-width: 1024px) {
      .event-logo {
        height: 38px;
        max-width: 160px;
      }
      
      .event-logo-fallback {
        height: 38px;
        width: 38px;
        font-size: 15px;
      }
    }
  `}</style>

    {/* Hero Section */}
    <div
      className="landing-preview-hero"
      style={{
        background: eventData.bannerImage
          ? `url(${eventData.bannerImage}) center/cover no-repeat, linear-gradient(135deg, ${colors[0] || "#f97316"}, ${colors[1] || "#fb923c"})`
          : `linear-gradient(135deg, ${colors[0] || "#f97316"}, ${colors[1] || "#fb923c"})`,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        color: "white",
        padding: isMobile ? "40px 16px" : "60px 20px",
        textAlign: "center",
        position: "relative",
      }}
    >
  {eventData.bannerImage && (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        zIndex: 1,
      }}
    />
      )}
      <div style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: "800px", margin: "0 auto" }}>
    <h1
      style={{
        fontSize: isMobile ? "2rem" : "3.5rem",
        marginBottom: "1rem",
        fontWeight: "bold",
        color: eventData.eventTitleColor || "white",
        lineHeight: "1.2",
      }}
    >
      {eventData.name || eventData.title || "Event Title"}
    </h1>
    <p style={{ 
      fontSize: isMobile ? "1rem" : "1.5rem", 
      marginBottom: "1.5rem", 
      opacity: 0.95,
      maxWidth: "100%",
    }}>
      {eventData.subtitle || (eventData.description ? eventData.description.substring(0, 60) + "..." : "Event Subtitle")}
    </p>
    {/* Countdown */}
    {countdown && !eventInPast && (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.75rem 1.5rem",
          borderRadius: "999px",
          backgroundColor: "rgba(15, 23, 42, 0.75)",
          marginBottom: "2rem",
          fontSize: isMobile ? "0.85rem" : "1rem",
        }}
      >
        <span style={{ fontWeight: 600 }}>Event starts in:</span>
        <span>
          {countdown.days}d {countdown.hours}h {countdown.minutes}m {countdown.seconds}s
        </span>
      </div>
    )}
    {eventInPast && (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "0.5rem 1.25rem",
          borderRadius: "999px",
          backgroundColor: "rgba(22, 163, 74, 0.9)",
          marginBottom: "2rem",
          fontSize: "0.95rem",
          fontWeight: 600,
        }}
      >
        This event has started or already took place.
      </div>
    )}
    {/* Event Details */}
    <div
      className="landing-preview-details"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: isMobile ? "1rem" : "2rem",
        justifyContent: "center",
        marginBottom: "2rem",
        fontSize: isMobile ? "0.9rem" : "1.1rem",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Calendar size={18} />
        {eventData.date ? (() => {
          try {
            const date = new Date(eventData.date);
            return isNaN(date.getTime()) ? eventData.date : date.toLocaleDateString();
          } catch {
            return eventData.date;
          }
        })() : "Date TBA"}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Clock size={18} />
        {eventData.time || "Time TBA"}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <MapPin size={18} />
        {eventData.location || "Location TBA"}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Users size={18} />
        {eventData.attendees || eventData.capacity || "0"} attendees
      </span>
    </div>
    
    {/* CTA Buttons - Centered on Mobile */}
      {!registrationClosed && (
        <button
          className="landing-preview-cta"
          onClick={handleRegisterNow}
          style={{
            padding: isMobile ? "14px 24px" : "1rem 2.5rem",
            fontSize: isMobile ? "1rem" : "1.2rem",
            fontWeight: "600",
            backgroundColor: "white",
            color: colors[0] || "#f97316",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "transform 0.2s",
            width: isMobile ? "100%" : "auto",
            maxWidth: isMobile ? "280px" : "none",
            margin: isMobile ? "1rem auto 0 auto" : "0.5rem 0.75rem 0 0",
            display: "inline-block",
          }}
          onMouseOver={(e) => (e.target.style.transform = "scale(1.05)")}
          onMouseOut={(e) => (e.target.style.transform = "scale(1)")}
        >
          Register Now
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          try {
            if (!eventData?.date) return;
            const eventDate = new Date(eventData.date);
            if (isNaN(eventDate.getTime())) return;
            // Derive start/end times
            let startTimeStr = "";
            let endTimeStr = "";
            if (eventData.time && typeof eventData.time === "string" && eventData.time.includes("-")) {
              const parts = eventData.time.split("-").map((p) => p.trim());
              startTimeStr = parts[0];
              endTimeStr = parts[1] || "";
            } else {
              startTimeStr = eventData.time || "";
              endTimeStr = eventData.endTime || "";
            }
            const parseTime = (timeStr) => {
              if (!timeStr) return null;
              const match = timeStr.match(/(\d{1,2}):?(\d{2})?/);
              if (!match) return null;
              const h = parseInt(match[1], 10);
              const m = match[2] ? parseInt(match[2], 10) : 0;
              if (isNaN(h) || isNaN(m)) return null;
              return { h, m };
            };
            const startParsed = parseTime(startTimeStr);
            const endParsed = parseTime(endTimeStr);
            const start = new Date(eventDate);
            if (startParsed) {
              start.setHours(startParsed.h, startParsed.m, 0, 0);
            }
            let end = new Date(start);
            if (endParsed) {
              end.setHours(endParsed.h, endParsed.m, 0, 0);
            } else {
              // Default to 1 hour duration if no end time
              end = new Date(start.getTime() + 60 * 60 * 1000);
            }
            const formatForGoogle = (dt) =>
              dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
            const datesParam = `${formatForGoogle(start)}/${formatForGoogle(end)}`;
            const title = eventData.name || eventData.title || "Event";
            const details = eventData.description || "";
            const location = locationString || eventData.location || "";
            const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
              title
            )}&dates=${encodeURIComponent(datesParam)}&details=${encodeURIComponent(
              details
            )}&location=${encodeURIComponent(location)}`;
            window.open(url, "_blank", "noopener,noreferrer");
          } catch (err) {
            console.error("Failed to open calendar link", err);
          }
        }}
        style={{
          padding: isMobile ? "14px 24px" : "0.9rem 2.1rem",
          fontSize: isMobile ? "1rem" : "1rem",
          fontWeight: 600,
          backgroundColor: "rgba(255, 255, 255, 0.09)",
          color: "#ffffff",
          border: "1px solid rgba(255,255,255,0.35)",
          borderRadius: "999px",
          cursor: "pointer",
          backdropFilter: "blur(12px)",
          boxShadow: "0 10px 30px rgba(15,23,42,0.5)",
          transition: "background-color 0.2s, transform 0.2s, box-shadow 0.2s, border-color 0.2s",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
          width: isMobile ? "100%" : "auto",
          maxWidth: isMobile ? "580px" : "300px",
          margin: isMobile ? "1rem auto 0 auto" : "0.5rem 0 0 0.75rem",
          minWidth: "auto",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.16)";
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 16px 40px rgba(15,23,42,0.7)";
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.7)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.09)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.5)";
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
        }}
      >
        <Calendar size={16} />
        Add to calendar
      </button>
  </div>
</div>

    {/* About Section */}
    <div
      className="landing-preview-section"
      ref={aboutRef}
      style={{
        padding: isMobile ? "40px 16px" : "80px 20px",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
        <h2
          style={{
            fontSize: isMobile ? "1.75rem" : "2.5rem",
            marginBottom: "1.5rem",
            textAlign: "center",
            color: "#1f2937",
          }}
        >
          About This Event
        </h2>
        <p
          style={{
            textAlign: "center",
            marginBottom: "3rem",
            color: "#6b7280",
            fontSize: isMobile ? "1rem" : "1.1rem",
            lineHeight: "1.8",
          }}
        >
          {eventData.description || "Event description will appear here."}
        </p>
        {/* Video Section */}
        {eventData.videoLink && (
          <div
            style={{
              marginBottom: "4rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <h3
              style={{
                fontSize: isMobile ? "1.5rem" : "2rem",
                marginBottom: "2rem",
                textAlign: "center",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Video size={24} />
              Event Video
            </h3>
            <div
              style={{
                position: "relative",
                width: "100%",
                maxWidth: "800px",
                paddingBottom: "56.25%",
                height: 0,
                overflow: "hidden",
                borderRadius: "12px",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                backgroundColor: "#000",
              }}
            >
              <iframe
                width="100%"
                height="100%"
                src={convertYouTubeUrlToEmbed(eventData.videoLink) || eventData.videoLink}
                title="Event Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>
          </div>
        )}
        {/* Features */}
        {(eventData.features && eventData.features.length > 0) && (
          <>
            <h3
              style={{
                fontSize: isMobile ? "1.5rem" : "2rem",
                marginBottom: "2rem",
                textAlign: "center",
                color: "#1f2937",
              }}
            >
              What to Expect
            </h3>
            <div
              className="landing-preview-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "1.5rem",
                marginBottom: "4rem",
              }}
            >
              {eventData.features.map((feature, index) => (
                <div
                  key={index}
                  className="landing-preview-feature"
                  style={{
                    padding: "1.5rem",
                    borderRadius: "12px",
                    backgroundColor: "#f9fafb",
                    border: `2px solid ${colors[2] || "#fdba74"}`,
                    textAlign: "center",
                  }}
                >
                  <span style={{ color: colors[0] || "#f97316", fontSize: "1.5rem", marginRight: "0.5rem" }}>
                    ✓
                  </span>
                  {feature}
                </div>
              ))}
            </div>
          </>
        )}
        {/* Speakers Section */}
        {sectionFlags.hasSpeakers && (
          <>
            <h3
              ref={speakersRef}
              style={{
                fontSize: isMobile ? "1.75rem" : "2rem",
                marginBottom: "2rem",
                textAlign: "center",
                color: "#1f2937",
              }}
            >
              Featured Speakers
            </h3>
            {speakerLayout === "centered-grid" ? (
              <div
                className="landing-preview-speakers centered-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "2rem",
                  marginBottom: "2rem",
                }}
              >
                {eventData.speakers.map((speaker, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "1.5rem",
                      borderRadius: "16px",
                      backgroundColor: "#ffffff",
                      border: `1px solid ${colors[2] || "#e5e7eb"}`,
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.08)",
                      textAlign: "center",
                    }}
                  >
                    {speaker.image ? (
                      <img
                        src={speaker.image}
                        loading="lazy"
                        decoding="async"
                        alt={speaker.name}
                        style={{
                          width: "96px",
                          height: "96px",
                          borderRadius: "999px",
                          objectFit: "cover",
                          marginBottom: "0.75rem",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "96px",
                          height: "96px",
                          borderRadius: "999px",
                          background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "2rem",
                          fontWeight: "700",
                          margin: "0 auto 0.75rem",
                        }}
                      >
                        {speaker.avatar || speaker.name?.charAt(0) || "?"}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "#111827",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {speaker.name || "Speaker Name"}
                    </div>
                    <div
                      style={{
                        fontSize: "0.95rem",
                        color: "#6b7280",
                        marginBottom: speaker.topic ? "0.35rem" : 0,
                      }}
                    >
                      {speaker.title || "Speaker Title"}
                      {speaker.company && ` • ${speaker.company}`}
                    </div>
                    {speaker.topic && (
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: colors[0] || "#f97316",
                          fontStyle: "italic",
                        }}
                      >
                        {speaker.topic}
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: "1rem",
                        fontSize: "0.85rem",
                        color: colors[0] || "#f97316",
                        fontWeight: 500,
                      }}
                    >
                      View Details →
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="landing-preview-speakers"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "stretch",
                  gap: "1.5rem",
                  marginBottom: "2rem",
                }}
              >
                {eventData.speakers.map((speaker, index) => (
                  <div
                    key={index}
                    className="landing-preview-speaker"
                    onClick={() => handleSpeakerClick(speaker)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1.5rem",
                      padding: "1.5rem",
                      borderRadius: "12px",
                      backgroundColor: "#f9fafb",
                      border: `1px solid ${colors[3] || "#fed7aa"}`,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      flex: "0 1 320px",
                      minWidth: "280px",
                      maxWidth: "400px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateX(4px)";
                      e.currentTarget.style.backgroundColor = "#ffffff";
                      e.currentTarget.style.boxShadow = "0 10px 25px rgba(15, 23, 42, 0.1)";
                      e.currentTarget.style.borderColor = colors[0] || "#f97316";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateX(0)";
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.borderColor = colors[3] || "#fed7aa";
                    }}
                  >
                    {speaker.image ? (
                      <img
                        src={speaker.image}
                        loading="lazy"
                        decoding="async"
                        alt={speaker.name}
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        className="landing-speaker-avatar"
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          backgroundColor: colors[0] || "#f97316",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                          flexShrink: 0,
                        }}
                      >
                        {speaker.avatar || speaker.name?.charAt(0) || "?"}
                      </div>
                    )}
                    <div>
                      <div
                        className="landing-speaker-name"
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "600",
                          color: "#1f2937",
                          marginBottom: "0.25rem",
                        }}
                      >
                        {speaker.name || "Speaker Name"}
                      </div>
                      <div
                        className="landing-speaker-title"
                        style={{
                          fontSize: "1rem",
                          color: "#6b7280",
                          marginBottom: speaker.company || speaker.topic ? "0.25rem" : "0",
                        }}
                      >
                        {speaker.title || "Speaker Title"}
                        {speaker.company && ` • ${speaker.company}`}
                      </div>
                      {speaker.topic && (
                        <div
                          style={{
                            fontSize: "0.9rem",
                            color: colors[0] || "#f97316",
                            fontStyle: "italic",
                          }}
                        >
                          {speaker.topic}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {/* Speaker Modal */}
        {isSpeakerModalOpen && selectedSpeaker && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: "20px",
              backdropFilter: "blur(4px)",
            }}
            onClick={closeSpeakerModal}
          >
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                maxWidth: "600px",
                width: "100%",
                maxHeight: "90vh",
                overflow: "auto",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
                position: "relative",
                animation: "fadeInScale 0.3s ease-out",
                transform: "scale(1)",
                opacity: 1,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeSpeakerModal}
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  background: "rgba(0, 0, 0, 0.1)",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  zIndex: 10,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.2)";
                  e.currentTarget.style.transform = "rotate(90deg)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.transform = "rotate(0deg)";
                }}
              >
                <X size={20} color="#1f2937" />
              </button>
              <div style={{ padding: "40px", textAlign: "center" }}>
                <div style={{ marginBottom: "24px" }}>
                  {selectedSpeaker.image ? (
                    <img
                      src={selectedSpeaker.image}
                      loading="lazy"
                      decoding="async"
                      alt={selectedSpeaker.name}
                      style={{
                        width: "160px",
                        height: "160px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: `4px solid ${colors[0] || "#f97316"}`,
                        boxShadow: `0 10px 30px rgba(249, 115, 22, 0.3)`,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "160px",
                        height: "160px",
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "4rem",
                        fontWeight: "700",
                        margin: "0 auto",
                        boxShadow: `0 10px 30px rgba(249, 115, 22, 0.3)`,
                      }}
                    >
                      {selectedSpeaker.avatar || selectedSpeaker.name?.charAt(0) || "?"}
                    </div>
                  )}
                </div>
                <h2
                  style={{
                    fontSize: "2rem",
                    fontWeight: "700",
                    color: "#1f2937",
                    marginBottom: "8px",
                  }}
                >
                  {selectedSpeaker.name || "Speaker Name"}
                </h2>
                <div
                  style={{
                    fontSize: "1.2rem",
                    color: colors[0] || "#f97316",
                    fontWeight: "600",
                    marginBottom: "8px",
                  }}
                >
                  {selectedSpeaker.title || "Speaker Title"}
                </div>
                {selectedSpeaker.company && (
                  <div
                    style={{
                      fontSize: "1rem",
                      color: "#6b7280",
                      marginBottom: "24px",
                    }}
                  >
                    {selectedSpeaker.company}
                  </div>
                )}
                {selectedSpeaker.topic && (
                  <div
                    style={{
                      fontSize: "1rem",
                      color: colors[0] || "#f97316",
                      lineHeight: "1.6",
                      marginBottom: "24px",
                      textAlign: "left",
                      padding: "16px",
                      backgroundColor: "#fff7ed",
                      borderRadius: "12px",
                      border: `1px solid ${colors[3] || "#fed7aa"}`,
                      fontWeight: 500,
                      fontStyle: "italic",
                    }}
                  >
                    "{selectedSpeaker.topic}"
                  </div>
                )}
                {selectedSpeaker.bio ? (
                  <>
                    <div
                      style={{
                        height: "1px",
                        background: `linear-gradient(to right, transparent, ${colors[2] || "#e5e7eb"}, transparent)`,
                        margin: "24px 0",
                      }}
                    />
                    <div style={{ marginBottom: "24px" }}>
                      <h4
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          color: "#1f2937",
                          marginBottom: "12px",
                        }}
                      >
                        About
                      </h4>
                      <div
                        style={{
                          fontSize: "1rem",
                          color: "#4b5563",
                          lineHeight: "1.7",
                          textAlign: "left",
                          padding: "20px",
                          backgroundColor: "#f9fafb",
                          borderRadius: "12px",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedSpeaker.bio}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        height: "1px",
                        background: `linear-gradient(to right, transparent, ${colors[2] || "#e5e7eb"}, transparent)`,
                        margin: "24px 0",
                      }}
                    />
                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "#9ca3af",
                        fontStyle: "italic",
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      More information about this speaker will be available soon.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Location Section */}
        {sectionFlags.hasLocation && (
          <div
            className="landing-preview-section"
            ref={locationRef}
            style={{
              padding: isMobile ? "40px 16px" : "80px 20px 40px",
              maxWidth: "900px",
              margin: "0 auto",
              marginTop: "2rem",
              textAlign: "center",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                marginBottom: "1rem",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <MapPin size={28} style={{ color: colors[0] || "#f97316" }} />
              Event Location
            </h2>
            <p
              style={{
                fontSize: isMobile ? "1rem" : "1.1rem",
                color: "#4b5563",
                marginBottom: "0.5rem",
              }}
            >
              {locationString || "Location details will be shared soon."}
            </p>
            {eventData.locationNote && (
              <p
                style={{
                  fontSize: "0.95rem",
                  color: "#6b7280",
                  marginTop: "0.25rem",
                }}
              >
                {eventData.locationNote}
              </p>
            )}
            {mapEmbedUrl && (
              <div
                style={{
                  marginTop: "2rem",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                  border: "1px solid #e5e7eb",
                }}
              >
                <iframe
                  title="Event location map"
                  src={mapEmbedUrl}
                  width="100%"
                  height="360"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            )}
          </div>
        )}
        {/* FAQ Section */}
        {Array.isArray(eventData.faq) && eventData.faq.length > 0 && (
          <div
            className="landing-preview-section"
            style={{
              padding: isMobile ? "40px 16px" : "80px 20px",
              maxWidth: "900px",
              margin: "0 auto",
              marginTop: "2rem",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                marginBottom: "1.5rem",
                textAlign: "center",
                color: "#1f2937",
              }}
            >
              Frequently Asked Questions
            </h2>
            <div
              style={{
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                overflow: "hidden",
              }}
            >
              {eventData.faq.map((faq, index) => (
                faq?.question && faq?.answer && (
                  <div
                    key={faq._id || index}
                    style={{
                      borderBottom:
                        index === eventData.faq.length - 1 ? "none" : "1px solid #e5e7eb",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenFaqIndex((prev) => (prev === index ? null : index))
                      }
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "1rem 1.5rem",
                        background: "none",
                        border: "none",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1rem",
                          fontWeight: 500,
                          color: "#111827",
                        }}
                      >
                        {faq.question}
                      </span>
                      <span
                        style={{
                          fontSize: "1.25rem",
                          color: "#9ca3af",
                          transform: openFaqIndex === index ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform 0.15s ease",
                        }}
                      >
                        ›
                      </span>
                    </button>
                    {openFaqIndex === index && (
                      <div
                        style={{
                          padding: "0 1.5rem 1rem 1.5rem",
                          fontSize: "0.95rem",
                          color: "#4b5563",
                          lineHeight: 1.6,
                        }}
                      >
                        {faq.answer}
                      </div>
                    )}
                  </div>
                )
              ))}
            </div>
          </div>
        )}
        {/* Sponsors Section */}
        {(() => {
          const sponsorsFromConfig = Array.isArray(eventData.sponsors) ? eventData.sponsors : [];
          const sponsorsFromApi = Array.isArray(sponsorsDataFromApi?.data) ? sponsorsDataFromApi.data : [];
          const sponsorsCombined = sponsorsFromConfig.length > 0 ? sponsorsFromConfig : sponsorsFromApi;
          if (!sponsorsCombined || sponsorsCombined.length === 0) return null;
          return (
            <div
              className="landing-preview-section"
              style={{
                padding: isMobile ? "40px 16px" : "80px 20px",
                maxWidth: "1200px",
                margin: "0 auto",
                marginTop: "2rem",
              }}
            >
              <h2
                style={{
                  fontSize: isMobile ? "1.75rem" : "2.5rem",
                  marginBottom: "1.5rem",
                  textAlign: "center",
                  color: "#1f2937",
                }}
              >
                Our Sponsors
              </h2>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: "1.5rem",
                }}
              >
                {sponsorsCombined.map((sponsor, index) => (
                  <div
                    key={sponsor.id || sponsor._id || sponsor.name || index}
                    style={{
                      padding: "1rem 1.5rem",
                      borderRadius: "12px",
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      minWidth: "180px",
                      maxWidth: "220px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {sponsor.logo && (
                      <img
                        src={sponsor.logo}
                        loading="lazy"
                        decoding="async"
                        alt={sponsor.name || "sponsor-logo"}
                        style={{
                          maxWidth: "160px",
                          maxHeight: "80px",
                          objectFit: "contain",
                        }}
                      />
                    )}
                    {sponsor.name && (
                      <span
                        style={{
                          fontSize: "0.95rem",
                          color: "#4b5563",
                          fontWeight: 500,
                          textAlign: "center",
                        }}
                      >
                        {sponsor.name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {/* Agenda Section */}
        {sectionFlags.hasAgenda && (
          <div
            className="landing-preview-section"
            ref={agendaRef}
            style={{
              padding: isMobile ? "40px 16px" : "80px 20px",
              maxWidth: "1100px",
              margin: "0 auto",
              marginTop: "4rem",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                marginBottom: "0.75rem",
                textAlign: "center",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <CalendarDays size={32} style={{ color: colors[0] || "#f97316" }} />
              Event Agenda
            </h2>
            <p
              style={{
                textAlign: "center",
                marginBottom: "2rem",
                color: "#6b7280",
                fontSize: isMobile ? "0.95rem" : "1rem",
              }}
            >
              A high-level overview of what will be happening throughout the event.
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              {eventData.agenda.map((item, index) => (
                <div
                  key={item._id || index}
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "center",
                    justifyContent: "space-between",
                    gap: isMobile ? "0.75rem" : "1.5rem",
                    padding: isMobile ? "1rem 1.25rem" : "1.25rem 1.75rem",
                    borderRadius: "16px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {/* Time column */}
                  <div
                    style={{
                      minWidth: isMobile ? "auto" : "120px",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: colors[0] || "#f97316",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {(item.startTime || item.endTime) ? (
                      <span>
                        {item.startTime}
                        {item.endTime && ` - ${item.endTime}`}
                      </span>
                    ) : (
                      <span>Time TBA</span>
                    )}
                  </div>
                  {/* Title & description */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.05rem",
                        fontWeight: 600,
                        color: "#111827",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {item.title || item.name || `Agenda item ${index + 1}`}
                    </div>
                    {item.description && (
                      <div
                        style={{
                          fontSize: "0.95rem",
                          color: "#6b7280",
                          lineHeight: 1.6,
                        }}
                      >
                        {item.description}
                      </div>
                    )}
                  </div>

                                {/* Add to calendar button */}
                                  {(() => {
                    const eventInfo = prepareEventData(eventData, item, locationString);
                    if (!eventInfo) return null;
                    return (
                      <div style={{ marginLeft: isMobile ? 0 : "1rem", alignSelf: isMobile ? "stretch" : "center" }}>
                        <CalendarDropdown
                          title={eventInfo.title}
                          description={eventInfo.description}
                          location={eventInfo.location}
                          start={eventInfo.start}
                          end={eventInfo.end}
                          isMobile={isMobile}
                          iconSize={14}
                          buttonStyle={{
                            padding: "0.55rem 1.1rem",
                            fontSize: "0.9rem",
                            fontWeight: 500,
                            backgroundColor: "#f97316",   // orange background
                            color: "#ffffff",              // white text/icon
                            border: "1px solid #f97316",
                            minWidth: "auto",
                          }}
                        />
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Sessions Section */}
        {sectionFlags.hasSchedule && (
          <div
            className="landing-preview-section"
            ref={scheduleRef}
            style={{
              padding: isMobile ? "40px 16px" : "80px 20px",
              maxWidth: "1200px",
              margin: "0 auto",
              marginTop: "4rem",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                marginBottom: "1.5rem",
                textAlign: "center",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <CalendarDays size={32} style={{ color: colors[0] || "#f97316" }} />
              Event Schedule
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "1.5rem",
                marginTop: "2rem",
              }}
            >
              {eventData.schedule.map((session, index) => (
                <div
                  key={index}
                  style={{
                    padding: "1.5rem",
                    borderRadius: "12px",
                    backgroundColor: "#f9fafb",
                    border: `2px solid ${colors[1] || "#fb923c"}`,
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.1)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <Clock size={20} style={{ color: colors[0] || "#f97316" }} />
                    <div>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: "600",
                          color: "#1f2937",
                          marginBottom: "0.25rem",
                        }}
                      >
                        {session.title || session.name || `Session ${index + 1}`}
                      </div>
                      {session.time && (
                        <div
                          style={{
                            fontSize: "0.9rem",
                            color: "#6b7280",
                          }}
                        >
                          {session.time}
                        </div>
                      )}
                    </div>
                  </div>
                  {session.date && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginBottom: "0.75rem",
                        fontSize: "0.9rem",
                        color: "#6b7280",
                      }}
                    >
                      <Calendar size={16} />
                      {new Date(session.date).toLocaleDateString()}
                    </div>
                  )}
                  {session.description && (
                    <p
                      style={{
                        fontSize: "0.95rem",
                        color: "#4b5563",
                        lineHeight: "1.6",
                        marginTop: "0.75rem",
                      }}
                    >
                      {session.description}
                    </p>
                  )}
                  {session.location && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginTop: "0.75rem",
                        fontSize: "0.9rem",
                        color: "#6b7280",
                      }}
                    >
                      <MapPin size={16} />
                      {session.location}
                    </div>
                  )}
                  {isSessionLive(session) && (session.sessionID || session._id) && (
                    <button
                      onClick={() => handleJoinSession(session)}
                      style={{
                        width: "100%",
                        marginTop: "1rem",
                        padding: "0.75rem 1.5rem",
                        backgroundColor: colors[0] || "#f97316",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "1rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        transition: "background-color 0.2s, transform 0.2s",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = colors[1] || "#fb923c";
                        e.currentTarget.style.transform = "scale(1.02)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = colors[0] || "#f97316";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <Play size={18} fill="white" />
                      Join Session
                    </button>
                  )}
                  {isSessionLive(session) && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginTop: "0.75rem",
                        padding: "0.25rem 0.75rem",
                        backgroundColor: "#ef4444",
                        color: "white",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                          animation: "pulse 2s infinite",
                        }}
                      />
                      LIVE
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Tickets Section */}
        {eventData.tickets && Array.isArray(eventData.tickets) && eventData.tickets.length > 0 && (
          <div
            className="landing-preview-section"
            style={{
              padding: isMobile ? "40px 16px" : "80px 20px",
              maxWidth: "1200px",
              margin: "0 auto",
              marginTop: "4rem",
              backgroundColor: "#f9fafb",
              borderRadius: "12px",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                marginBottom: "1.5rem",
                textAlign: "center",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <Ticket size={32} style={{ color: colors[0] || "#f97316" }} />
              Available Tickets
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "2rem",
                marginTop: "2rem",
              }}
            >
              {eventData.tickets.map((ticket, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2rem",
                    borderRadius: "12px",
                    backgroundColor: "white",
                    border: `2px solid ${colors[2] || "#fdba74"}`,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.15)";
                    e.currentTarget.style.borderColor = colors[0] || "#f97316";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.05)";
                    e.currentTarget.style.borderColor = colors[2] || "#fdba74";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                    <div
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#1f2937",
                      }}
                    >
                      {ticket.name || ticket.title || `Ticket ${index + 1}`}
                    </div>
                    {ticket.isEarlyBird && (
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          color: "white",
                          backgroundColor: colors[0] || "#f97316",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "12px",
                        }}
                      >
                        Early Bird
                      </span>
                    )}
                  </div>
                  {ticket.description && (
                    <p
                      style={{
                        fontSize: "0.95rem",
                        color: "#6b7280",
                        lineHeight: "1.6",
                        marginBottom: "1rem",
                        flexGrow: 1,
                      }}
                    >
                      {ticket.description}
                    </p>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "0.5rem",
                      marginTop: "auto",
                      paddingTop: "1rem",
                      borderTop: "1px solid #e5e7eb",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "2rem",
                        fontWeight: "700",
                        color: colors[0] || "#f97316",
                      }}
                    >
                      {ticket.price ? (typeof ticket.price === 'number' ? `R${ticket.price.toFixed(2)}` : ticket.price) : "Free"}
                    </span>
                    {ticket.price && (
                      <span
                        style={{
                          fontSize: "0.9rem",
                          color: "#6b7280",
                        }}
                      >
                        per ticket
                      </span>
                    )}
                  </div>
                  {ticket.features && Array.isArray(ticket.features) && ticket.features.length > 0 && (
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        margin: "1rem 0",
                      }}
                    >
                      {ticket.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          style={{
                            fontSize: "0.9rem",
                            color: "#4b5563",
                            marginBottom: "0.5rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <span style={{ color: colors[0] || "#f97316" }}>✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!registrationClosed && (
                    <button
                      onClick={handleRegisterNow}
                      style={{
                        marginTop: "1rem",
                        padding: "0.75rem 1.5rem",
                        fontSize: "1rem",
                        fontWeight: "600",
                        backgroundColor: colors[0] || "#f97316",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "background-color 0.2s, transform 0.2s",
                        width: "100%",
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = colors[1] || "#fb923c";
                        e.target.style.transform = "scale(1.02)";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = colors[0] || "#f97316";
                        e.target.style.transform = "scale(1)";
                      }}
                    >
                      Select Ticket
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Support Section */}
        {sectionFlags.hasSupport && (
          <div
            ref={supportRef}
            className="landing-preview-section"
            style={{
              padding: isMobile ? "40px 16px" : "80px 20px",
              maxWidth: "1200px",
              margin: "0 auto",
              marginTop: "4rem",
              backgroundColor: "#f9fafb",
              borderRadius: "16px",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                marginBottom: "2rem",
                textAlign: "center",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <Mail size={32} style={{ color: colors[0] || "#f97316" }} />
              Event Support
            </h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              {eventData.supportMail && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "1.5rem",
                    backgroundColor: "white",
                    borderRadius: "12px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                  }}
                >
                  <Mail size={24} style={{ color: colors[0] || "#f97316" }} />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#6b7280",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Email
                    </div>
                    <a
                      href={`mailto:${eventData.supportMail}`}
                      style={{
                        color: colors[0] || "#f97316",
                        textDecoration: "none",
                        fontWeight: "600",
                        fontSize: "1.1rem",
                      }}
                      onMouseOver={(e) => {
                        e.target.style.textDecoration = "underline";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.textDecoration = "none";
                      }}
                    >
                      {eventData.supportMail}
                    </a>
                  </div>
                </div>
              )}
              {eventData.supportPhoneNumber && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "1.5rem",
                    backgroundColor: "white",
                    borderRadius: "12px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                  }}
                >
                  <Phone size={24} style={{ color: colors[0] || "#f97316" }} />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#6b7280",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Phone
                    </div>
                    <a
                      href={`tel:${eventData.supportPhoneNumber}`}
                      style={{
                        color: colors[0] || "#f97316",
                        textDecoration: "none",
                        fontWeight: "600",
                        fontSize: "1.1rem",
                      }}
                      onMouseOver={(e) => {
                        e.target.style.textDecoration = "underline";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.textDecoration = "none";
                      }}
                    >
                      {eventData.supportPhoneNumber}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Registration Form Section */}
        {effectiveRegistrationForm && effectiveRegistrationForm.length > 0 && !registrationClosed && (
          <div
            ref={registrationFormRef}
            id="registration-form-section"
            className="landing-preview-section"
            style={{
              padding: isMobile ? "40px 16px" : "80px 20px",
              maxWidth: "1200px",
              margin: "0 auto",
              backgroundColor: "#f9fafb",
              borderRadius: "12px",
              marginTop: "4rem",
              scrollMarginTop: "80px",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                marginBottom: "1.5rem",
                textAlign: "center",
                color: "#1f2937",
              }}
            >
              Register for the Event
            </h2>
            {registrationSuccess && (
              <div
                style={{
                  marginBottom: "1.5rem",
                  padding: "1rem 1.25rem",
                  borderRadius: "8px",
                  backgroundColor: "#ecfdf3",
                  border: "1px solid #22c55e",
                  color: "#166534",
                  fontSize: "0.95rem",
                  textAlign: "center",
                }}
              >
                Thank you for registering! Please check your email for your event entrance details.
              </div>
            )}
            {registrationError && (
              <div
                style={{
                  marginBottom: "1.5rem",
                  padding: "1rem 1.25rem",
                  borderRadius: "8px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #ef4444",
                  color: "#991b1b",
                  fontSize: "0.95rem",
                  textAlign: "center",
                }}
              >
                <strong>Registration Error:</strong> {registrationError}
              </div>
            )}
            <div className="landing-registration-form-display">
              {effectiveRegistrationForm.map((field, index) => {
                const displayLabel = formatLabel(field?.label, field?.name);
                const isPhoneField = field.name === "phoneNumber";
                return (
                  <div key={index} style={{ marginBottom: "1rem" }}>
                    {field.question_type !== "checkbox" &&
                      field.question_type !== "privacy_disclaimer" && (
                      <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600" }}>
                        {displayLabel}
                      </label>
                    )}
                    {isPhoneField ? (
                      <div className="phone-input-container">
                        <PhoneInput
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="ZA"
                          className="custom-phone-input"
                          value={formValues[field.name] || ""}
                          onChange={(value) => handleFieldChange(field.name, value)}
                        />
                      </div>
                    ) : field.question_type === 'text' || field.question_type === 'short_answer' ? (
                      <input
                        type="text"
                        name={field.name}
                        placeholder={displayLabel}
                        value={formValues[field.name] || ""}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                        }}
                      />
                    ) : field.question_type === 'paragraph' ? (
                      <textarea
                        name={field.name}
                        rows={4}
                        placeholder={displayLabel}
                        value={formValues[field.name] || ""}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                        }}
                      />
                    ) : field.question_type === 'options' ? (
                      <select
                        name={field.name}
                        value={formValues[field.name] || ""}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          backgroundColor: "white",
                        }}
                      >
                        <option value="">Select...</option>
                        {field.list && field.list.map((option, i) => (
                          <option key={i} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : field.question_type === 'checkbox' ? (
                      <label style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontWeight: "500",
                        cursor: "pointer",
                        margin: 0,
                      }}>
                        <input
                          type="checkbox"
                          name={field.name}
                          checked={!!formValues[field.name]}
                          onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                          style={{ width: "18px", height: "18px" }}
                        />
                        {displayLabel}
                        {field.required && <span style={{ color: "#dc2626" }}>*</span>}
                      </label>
                    ) : field.question_type === "privacy_disclaimer" ? (
                      <PrivacyDisclaimerText linkUrl={field.linkUrl} />
                    ) : null}
                    {formErrors[field.name] && (
                      <div style={{ color: "#dc2626", marginTop: "0.25rem", fontSize: "0.875rem" }}>
                        {formErrors[field.name]}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                style={{
                  padding: isMobile ? "0.875rem 2rem" : "1rem 2.5rem",
                  fontSize: isMobile ? "1rem" : "1.2rem",
                  fontWeight: "600",
                  backgroundColor: colors[0] || "#f97316",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  width: "100%",
                  marginTop: "1.5rem",
                }}
                onMouseOver={(e) => (e.target.style.transform = "scale(1.02)")}
                onMouseOut={(e) => (e.target.style.transform = "scale(1)")}
                onClick={handleInlineRegistrationSubmit}
                disabled={pending}
              >
                {pending ? "Submitting..." : "Submit Registration"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(CustomLandingPage);